import React, { useState, useEffect } from 'react';

export default function SweepstakesModal(props) {
  const { isOpen, onClose, monthLabel, users, topReferrers } = props;
  const [winner, setWinner] = useState(null);
  const [eligiblePool, setEligiblePool] = useState([]);
  const [uniqueParticipants, setUniqueParticipants] = useState([]);

  // Build eligible pool when modal opens or data changes
  useEffect(() => {
    if (isOpen) {
      const pool = buildEligiblePool(users, topReferrers);
      setEligiblePool(pool);
      setUniqueParticipants(getUniqueParticipants(pool));
      setWinner(null); // Reset winner when modal opens
    }
  }, [isOpen, users, topReferrers]);

  // Build eligible pool with multiple entries per contribution
  function buildEligiblePool(users, topReferrers) {
    const pool = [];

    // Add 1 entry for each signup
    users?.forEach(user => {
      pool.push({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        username: user.username,
        email: user.email,
        reason: 'signup'
      });
    });

    // Add 1 entry for EACH referral (person who referred gets entries = referral count)
    topReferrers?.forEach(referrer => {
      const referralCount = referrer.referral_count || 0;
      for (let i = 0; i < referralCount; i++) {
        pool.push({
          id: referrer.id,
          firstName: referrer.firstName,
          lastName: referrer.lastName,
          username: referrer.username,
          email: referrer.email || '',
          reason: 'referral',
          entryNumber: i + 1 // Track which referral entry this is
        });
      }
    });

    return pool;
  }

  // Random selection using cryptographic randomness
  function selectRandomWinner(pool) {
    if (!pool || pool.length === 0) return null;

    // Fisher-Yates shuffle for fairness
    const shuffled = [...pool];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const randomBuffer = new Uint32Array(1);
      crypto.getRandomValues(randomBuffer);
      const j = Math.floor((randomBuffer[0] / (0xFFFFFFFF + 1)) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled[0];
  }

  // Group pool entries by unique participant for display
  function getUniqueParticipants(pool) {
    const participantMap = new Map();

    pool.forEach(entry => {
      if (!participantMap.has(entry.id)) {
        participantMap.set(entry.id, {
          id: entry.id,
          firstName: entry.firstName,
          lastName: entry.lastName,
          username: entry.username,
          email: entry.email,
          signupEntries: 0,
          referralEntries: 0,
          totalEntries: 0
        });
      }

      const participant = participantMap.get(entry.id);
      if (entry.reason === 'signup') {
        participant.signupEntries++;
      } else if (entry.reason === 'referral') {
        participant.referralEntries++;
      }
      participant.totalEntries++;
    });

    return Array.from(participantMap.values()).sort((a, b) => b.totalEntries - a.totalEntries);
  }

  const handleDrawWinner = () => {
    const selected = selectRandomWinner(eligiblePool);
    setWinner(selected);
  };

  const handleRedraw = () => {
    const selected = selectRandomWinner(eligiblePool);
    setWinner(selected);
  };

  const handleClose = () => {
    setWinner(null);
    onClose();
  };

  // Format entry description for a participant
  const getEntryDescription = (participant) => {
    if (participant.signupEntries > 0 && participant.referralEntries > 0) {
      return `${participant.totalEntries} entries: 1 for signup + ${participant.referralEntries} for referrals`;
    } else if (participant.signupEntries > 0) {
      return '1 entry: Signed up this month';
    } else if (participant.referralEntries > 0) {
      const plural = participant.referralEntries === 1 ? 'person' : 'people';
      return `${participant.referralEntries} ${participant.referralEntries === 1 ? 'entry' : 'entries'}: Referred ${participant.referralEntries} ${plural}`;
    }
    return '';
  };

  // Get winner's entry details
  const getWinnerDetails = () => {
    if (!winner) return null;

    const participant = uniqueParticipants.find(p => p.id === winner.id);
    if (!participant) return null;

    return {
      ...participant,
      entryDescription: getEntryDescription(participant)
    };
  };

  if (!isOpen) return null;

  const winnerDetails = getWinnerDetails();

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={handleClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75" />
        </div>

        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div
          className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900" id="modal-headline">
              Monthly Sweepstakes - {monthLabel}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Stats */}
          <div className="mb-6 text-sm text-gray-600">
            {eligiblePool.length === 0 ? (
              <p>No eligible entries for this month</p>
            ) : (
              <p>
                <span className="font-semibold text-gray-900">{eligiblePool.length}</span> total entries from{' '}
                <span className="font-semibold text-gray-900">{uniqueParticipants.length}</span> participants
              </p>
            )}
          </div>

          {/* Winner Section */}
          {winner && winnerDetails && (
            <div className="mb-6 bg-green-50 border-2 border-green-500 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-lg font-bold text-green-900">
                    🎉 WINNER
                  </h4>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {winnerDetails.firstName} {winnerDetails.lastName}
                  </p>
                  <p className="text-sm text-gray-600">
                    @{winnerDetails.username}
                  </p>
                  {winnerDetails.email && (
                    <p className="text-sm text-gray-600">
                      {winnerDetails.email}
                    </p>
                  )}
                  <p className="mt-2 text-sm font-medium text-green-800">
                    {winnerDetails.entryDescription}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Participants List */}
          {eligiblePool.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">All Participants:</h4>
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                {uniqueParticipants.map((participant) => (
                  <div
                    key={participant.id}
                    className={`p-3 border-b border-gray-200 last:border-b-0 ${
                      winner && winner.id === participant.id ? 'bg-green-50' : 'bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {participant.firstName} {participant.lastName}
                        </p>
                        <p className="text-sm text-gray-600">
                          @{participant.username}
                        </p>
                        {participant.email && (
                          <p className="text-sm text-gray-600">
                            {participant.email}
                          </p>
                        )}
                        <p className="mt-1 text-sm text-gray-700">
                          {getEntryDescription(participant)}
                        </p>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {participant.totalEntries} {participant.totalEntries === 1 ? 'entry' : 'entries'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Close
            </button>
            {winner && (
              <button
                type="button"
                onClick={handleRedraw}
                disabled={eligiblePool.length === 0}
                className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Re-draw
              </button>
            )}
            <button
              type="button"
              onClick={handleDrawWinner}
              disabled={winner !== null || eligiblePool.length === 0}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Draw Winner
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
